import { useCallback, useEffect, useRef, useState } from "react";
import { classifyEmail, type EmailInsight } from "./insights";
import type { EmailContext } from "./office-mail";
import type { AddinSettings } from "./settings";
import type { MailboxAdapter } from "./mailbox-adapter";
import type { OrgPolicy } from "./policy";
import { BlockedByClassificationError, classificationBlockMessage } from "./data-classification";
import { AiError } from "./ai-client";
import { isErrorCode, type ErrorCode } from "./error-codes";
import { debugLog } from "./debug";

// Used while the mailbox adapter hasn't finished loading yet — the classification
// gate then falls back to the content heuristic alone (no label to read), same
// as an adapter that reports no label. See data-classification.ts.
const NO_ADAPTER: Pick<MailboxAdapter, "getSensitivityLabel"> = {
  getSensitivityLabel: async () => null,
};

// Stable module-level default: an inline `= {}` default parameter would
// create a fresh object identity on every call, which would in turn make the
// `run` useCallback below (deps include `policy`) recreate itself every
// render — retriggering the auto-analyze effect in a loop.
const DEFAULT_POLICY: OrgPolicy = {};

export type InsightState = "idle" | "loading" | "error" | "blocked";

export interface UseEmailInsight {
  insight: EmailInsight | null;
  state: InsightState;
  /** Generic, non-leaking message set only when `state === "blocked"` (ADR-005). */
  blockedMessage: string | null;
  /**
   * User-facing explanation set only when `state === "error"`, and only when the
   * failure was an {@link AiError} — those messages are authored for display
   * (e.g. `providerUnreachableMessage`). Any other rejection leaves this `null`
   * so the UI falls back to a generic string: per CLAUDE.md A3/B3 a raw internal
   * error must never reach the user.
   */
  errorMessage: string | null;
  /** Stable, documented code for the same failure (error-codes.ts), for the
   * user to quote or look up. `null` under the same conditions as
   * {@link errorMessage}. */
  errorCode: ErrorCode | null;
  analyze: () => void;
  refresh: () => void;
}

/** Session-scoped identity key for an open email. EmailContext has no id, so a
 * from+subject composite distinguishes the open message within a session. */
function keyOf(context: EmailContext): string {
  return `${context.from} ${context.subject}`;
}

/**
 * Classifies the open email into an `EmailInsight`, caching per-email for the
 * session. Auto-runs on context change only when `autoAnalyze` is true; otherwise
 * classification is manual via `analyze()`. `refresh()` bypasses the cache.
 * Failures set `state="error"` and are logged (never the body) — never thrown.
 */
export function useEmailInsight(
  context: EmailContext | null,
  settings: AddinSettings,
  autoAnalyze: boolean,
  adapter: Pick<MailboxAdapter, "getSensitivityLabel"> | null = null,
  policy: OrgPolicy = DEFAULT_POLICY
): UseEmailInsight {
  const cache = useRef<Map<string, EmailInsight>>(new Map());
  const [insight, setInsight] = useState<EmailInsight | null>(null);
  const [state, setState] = useState<InsightState>("idle");
  const [blockedMessage, setBlockedMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<ErrorCode | null>(null);

  const run = useCallback(
    async (ctx: EmailContext, force: boolean) => {
      const key = keyOf(ctx);
      if (!force && cache.current.has(key)) {
        setInsight(cache.current.get(key)!);
        setState("idle");
        return;
      }
      setState("loading");
      setBlockedMessage(null);
      setErrorMessage(null);
      setErrorCode(null);
      try {
        const result = await classifyEmail(ctx, settings, adapter ?? NO_ADAPTER, policy);
        cache.current.set(key, result);
        setInsight(result);
        setState("idle");
      } catch (error) {
        if (error instanceof BlockedByClassificationError) {
          setBlockedMessage(classificationBlockMessage(error));
          setState("blocked");
          return;
        }
        // Only an AiError carries a message written to be read by a user;
        // anything else (a TypeError from a bug, say) stays internal and the
        // banner shows its generic fallback instead.
        if (error instanceof AiError) {
          setErrorMessage(error.message);
          setErrorCode(isErrorCode(error.errorCode) ? error.errorCode : null);
        }
        // Never log the body/PII — only that classification failed.
        debugLog("insight:classify-failed", { error: String(error) });
        setState("error");
      }
    },
    [settings, adapter, policy]
  );

  // Auto-classify on context change when opted in; cache hit avoids a re-call.
  useEffect(() => {
    if (!autoAnalyze || !context) return;
    const key = keyOf(context);
    if (cache.current.has(key)) {
      setInsight(cache.current.get(key)!);
      setState("idle");
      return;
    }
    void run(context, false);
  }, [context, autoAnalyze, run]);

  const analyze = useCallback(() => {
    if (context) void run(context, false);
  }, [context, run]);

  const refresh = useCallback(() => {
    if (context) {
      cache.current.delete(keyOf(context));
      void run(context, true);
    }
  }, [context, run]);

  return { insight, state, blockedMessage, errorMessage, errorCode, analyze, refresh };
}
